import { useMemo } from 'react';
import { useLearning } from '../../../../lib/api/swrApi';
import { useId } from '../../../../lib/hooks/useId';
import SuggestedTrainingView from '../../base/SuggestedTrainingView/SuggestedTrainingView';
import { WithMappedAEMProperties } from '../../../../types';
import { ApiSwrGetResponse } from '../../../../lib/api/apiConfig';
import { SUGGESTED_TRAINING_API_LIMIT } from '../../../../lib/constants/courses';
import { useDevice } from '../../../../lib/hooks/device';
import SkeletonCarousel from '../../base/Carousel/SkeletonCarousel';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import { Api } from '../../../../lib/types/apiSpec';

export type SuggestedTrainingProps = WithMappedAEMProperties<{
  limit?: number;
  heading?: string;
}>;

export const validateAndMapData = (data: NonNullable<ApiSwrGetResponse<'/learning'>>) => {
  return data
    .filter(({ type }) => !!type)
    .map(({ id: cardId, type, name = '', description = '', duration, url = '', originalId, ...rest }) => ({
      ...rest,
      id: cardId,
      type: type as Api.TrainingType,
      name,
      description,
      duration,
      href: url,
      originalId,
    }));
};

const SuggestedTraining = ({ limit = 6, heading, id }: SuggestedTrainingProps) => {
  const device = useDevice();
  const { data, isValidating, error } = useLearning(SUGGESTED_TRAINING_API_LIMIT);
  const componentId = id ?? useId('suggested-training-');

  const validatedItems = data ? validateAndMapData(data) : [];
  const deviceBasedLimit = device === 'desktop' ? limit : Math.min(limit, 6);

  const randomisedItems = [...validatedItems].sort(() => Math.random() - 0.5);
  const randomisedSlice = useMemo(() => randomisedItems.slice(0, deviceBasedLimit) ?? [], [data]);

  if (!data?.length && isValidating) {
    return <SkeletonCarousel />;
  }

  if (error) {
    return <ErrorInfo error={error} />;
  }

  if (data === undefined || !data.length) {
    return null;
  }

  return <SuggestedTrainingView id={componentId} title={heading} items={randomisedSlice} />;
};

export const SuggestedTrainingEmptyFn = (props: SuggestedTrainingProps) => {
  if (!props || props.limit === undefined) {
    return true;
  }

  return false;
};

export default SuggestedTraining;
